import { useEffect, useRef, useState } from "react";
import { useClerk } from "@clerk/react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ShieldCheck } from "@phosphor-icons/react";
import { useApi, errText } from "@/lib/api";
import { CodeInput } from "@/components/account/BackupCodes";

export const deviceKey = (uid) => `cr-mfa-device:${uid}`;

export const MfaChallenge = ({ method, uid }) => {
  const api = useApi();
  const qc = useQueryClient();
  const { signOut } = useClerk();
  const [code, setCode] = useState("");
  const [backup, setBackup] = useState(false);
  const [trust, setTrust] = useState(false);
  const [sentTo, setSentTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(true);
  const sent = useRef(false);

  const send = async (auto) => {
    try { setSentTo((await api.post("/mfa/challenge")).sent_to); }
    catch (e) {
      if (auto && e?.response?.status === 429) setSentTo("your email (sent less than a minute ago)");
      else toast.error(errText(e, "Couldn't send a code"));
    }
  };
  useEffect(() => {
    if (sent.current) return;
    sent.current = true;
    (async () => {
      const legacy = localStorage.getItem(deviceKey(uid));
      try {
        const ok = (await api.post("/mfa/device", legacy ? { token: legacy } : {})).verified;
        if (legacy) localStorage.removeItem(deviceKey(uid));
        if (ok) return qc.invalidateQueries();
      } catch { /* fall through to a code */ }
      setChecking(false);
      if (method === "email") send(true);
    })();
  }, [method]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/mfa/verify", { code, trust_device: trust });
      if (r.used === "backup") toast(`Backup code used. ${r.backup_codes_left} left.`);
      await qc.invalidateQueries();
    } catch (err) { toast.error(errText(err, "That code didn't work")); setCode(""); }
    finally { setBusy(false); }
  };

  if (checking) return <p data-testid="mfa-device-checking" className="mx-auto mt-24 max-w-sm text-center font-mono text-xs text-[#A3A3A3]">Checking this trusted device…</p>;

  const hint = backup ? "Enter one of your backup codes."
    : method === "email" ? (sentTo ? `We emailed a 6-digit code to ${sentTo}.` : "Sending a code to your email…")
      : "Enter the 6-digit code from your authenticator app.";
  return (
    <form data-testid="mfa-challenge" onSubmit={submit} className="mx-auto mt-16 max-w-sm rounded-xl border border-[#2A2A2A] bg-[#121212] p-8">
      <ShieldCheck size={28} className="text-[#00FF9D]" />
      <h1 className="mt-4 font-display text-2xl font-semibold">Two-step sign-in</h1>
      <p data-testid="mfa-challenge-hint" className="mt-2 text-sm text-[#A3A3A3]">{hint}</p>
      <div className="mt-6"><CodeInput testid="mfa-challenge-input" value={code} onChange={setCode} backup={backup} /></div>
      <label className="mt-4 flex items-center gap-2 text-xs text-[#A3A3A3] cursor-pointer">
        <input type="checkbox" data-testid="mfa-trust-device" checked={trust} onChange={(e) => setTrust(e.target.checked)} className="h-4 w-4 accent-[#00FF9D]" />
        Trust this device for 30 days (don't tick on a shared computer)
      </label>
      <button data-testid="mfa-challenge-submit" disabled={busy || code.replace(/\W/g, "").length < 6}
        className="mt-4 w-full rounded-full bg-[#00FF9D] py-3 text-sm font-semibold text-[#050505] disabled:opacity-40">{busy ? "Checking…" : "Continue"}</button>
      <div className="mt-5 flex flex-wrap justify-between gap-2 text-xs">
        <button type="button" data-testid="mfa-use-backup" onClick={() => { setBackup((b) => !b); setCode(""); }} className="text-[#A3A3A3] hover:text-white underline">
          {backup ? "Use my normal code" : "Use a backup code"}</button>
        {method === "email" && !backup && <button type="button" data-testid="mfa-resend" onClick={() => send(false)} className="text-[#A3A3A3] hover:text-white underline">Send a new code</button>}
        <button type="button" data-testid="mfa-signout" onClick={() => signOut({ redirectUrl: "/" })} className="text-[#6B6B6B] hover:text-white">Sign out</button>
      </div>
    </form>
  );
};
